/**
 * /platform/access — хто має доступ до панелі платформи (ч.1125).
 *
 * Рівні: «власник платформи» (системні дані всіх клієнтів + звернення),
 * «співробітник підтримки» (лише звернення) і з ч.1319 «редактор відео-інструкцій»
 * (лише розділ відео: ролики до сторінок, зауваження, налаштування, YouTube). До ч.1125 ознаки ставились
 * вручну в базі. Кожну зміну власник підтверджує своїм паролем; рівень
 * «власник» видає і забирає лише головний власник (пошта з налаштувань
 * сервера), себе і головного забрати не можна — це вирішує сервер, сторінка
 * лише пояснює, чому кнопка неактивна.
 */
import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';
import {
  platformAccess,
  type PlatformAccessLevel,
  type PlatformAccessLogRow,
  type PlatformAccessUser,
} from '@/api/platformAccess';
import { extractApiError } from '@/utils/extractApiError';

const LEVEL_LABEL: Record<PlatformAccessLevel, string> = {
  OWNER: 'власник платформи',
  SUPPORT: 'співробітник підтримки',
  VIDEO: 'редактор відео-інструкцій',
};

const fmtDT = (v: string | null) => (v ? new Date(v).toLocaleString('uk-UA') : '—');

/** Чому доступ цього користувача не можна забрати (undefined — можна). */
export function revokeBlockedReason(u: PlatformAccessUser, canManageOwners: boolean): string | undefined {
  if (u.level !== 'OWNER') return undefined;
  if (u.is_root) return 'Головний власник: його пошта задана в налаштуваннях сервера';
  if (u.is_me) return 'Власний доступ забрати не можна — це робить інший власник';
  if (!canManageOwners) return 'Доступ власника платформи забирає лише головний власник';
  return undefined;
}

export default function PlatformAccessPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['platform-access'], queryFn: platformAccess.overview });
  const [email, setEmail] = useState('');
  const [level, setLevel] = useState<PlatformAccessLevel>('SUPPORT');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<PlatformAccessUser | null>(null);
  const [revokePassword, setRevokePassword] = useState('');
  const [revokeError, setRevokeError] = useState<string | null>(null);

  const canManageOwners = !!q.data?.can_manage_owners;

  const grant = useMutation({
    mutationFn: () => platformAccess.grant(email.trim(), level, password),
    onSuccess: () => {
      setDone(`Доступ надано: ${email.trim()} — ${LEVEL_LABEL[level]}. `
        + 'Панель відкриється після входу, підтвердженого кодом двофакторного входу.');
      setEmail(''); setPassword(''); setError(null);
      qc.invalidateQueries({ queryKey: ['platform-access'] });
    },
    onError: (e) => { setDone(null); setError(extractApiError(e, 'Не вдалося надати доступ')); },
  });
  const revoke = useMutation({
    mutationFn: () => platformAccess.revoke(revoking!.id, revoking!.level, revokePassword),
    onSuccess: () => {
      setDone(`Доступ забрано: ${revoking!.email}.`);
      setRevoking(null); setRevokePassword(''); setRevokeError(null);
      qc.invalidateQueries({ queryKey: ['platform-access'] });
    },
    onError: (e) => setRevokeError(extractApiError(e, 'Не вдалося забрати доступ')),
  });

  function onGrant(e: FormEvent) {
    e.preventDefault();
    setError(null); setDone(null);
    grant.mutate();
  }

  const columns: ColumnDef<PlatformAccessUser>[] = [
    {
      key: 'user', header: 'Користувач', sortValue: (u) => u.email,
      cell: (u) => (
        <span>
          {u.full_name}
          <span style={{ color: 'var(--text-secondary)' }}> · {u.email}</span>
          {u.is_me && <span style={{ color: 'var(--text-secondary)' }}> (це ви)</span>}
        </span>
      ),
    },
    {
      key: 'level', header: 'Рівень', width: 220, sortValue: (u) => u.level,
      cell: (u) => `${LEVEL_LABEL[u.level]}${u.is_root ? ' — головний' : ''}`
        + `${u.also_video ? ' + редактор відео' : ''}`,
    },
    {
      key: 'two_factor', header: 'Двофакторний вхід', width: 170,
      sortValue: (u) => (u.two_factor ? 1 : 0),
      cell: (u) => (u.two_factor
        ? 'налаштовано'
        : <span title="Без двофакторного входу панель не відкриється — сторінка панелі запропонує його налаштувати"
            style={{ color: 'var(--badge-amber-fg)' }}>не налаштовано</span>),
    },
    {
      key: 'last_login', header: 'Останній вхід', width: 170,
      sortValue: (u) => u.last_login_at ?? '', cell: (u) => fmtDT(u.last_login_at),
    },
    {
      key: 'actions', header: '', width: 260, align: 'right',
      cell: (u) => {
        const blocked = revokeBlockedReason(u, canManageOwners);
        const start = (row: PlatformAccessUser) => {
          setRevoking(row); setRevokePassword(''); setRevokeError(null); setDone(null);
        };
        return (
          <span style={{ display: 'inline-flex', gap: '0.35rem' }}>
            {u.also_video && (
              <button type="button" title="Забрати лише роль редактора відео-інструкцій"
                onClick={() => start({ ...u, level: 'VIDEO' })}>
                забрати відео
              </button>
            )}
            <button type="button" disabled={!!blocked} title={blocked} onClick={() => start(u)}>
              забрати доступ
            </button>
          </span>
        );
      },
    },
  ];

  const logColumns: ColumnDef<PlatformAccessLogRow>[] = [
    { key: 'at', header: 'Коли', width: 170, sortValue: (r) => r.created_at, cell: (r) => fmtDT(r.created_at) },
    { key: 'action', header: 'Дія', width: 110, cell: (r) => (r.action === 'GRANT' ? 'надано' : 'забрано') },
    { key: 'level', header: 'Рівень', width: 220, cell: (r) => LEVEL_LABEL[r.level] },
    { key: 'target', header: 'Кому', cell: (r) => r.target_email },
    { key: 'by', header: 'Хто', cell: (r) => r.performed_by_email },
  ];

  return (
    <>
      <div className="card">
        <h2 style={{ marginTop: 0 }}>Доступ до платформи</h2>
        <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', marginTop: 0 }}>
          <b>Власник платформи</b> бачить і змінює системні дані для всіх клієнтів
          і працює зі зверненнями. <b>Співробітник підтримки</b> — лише звернення
          користувачів і аналітика. Доступ дається зареєстрованому користувачу за
          його поштою; панель відкривається після входу, підтвердженого кодом
          двофакторного входу.
        </p>
        <DataTable
          rows={q.data?.items ?? []}
          columns={columns}
          rowKey={(u) => u.id}
          loading={q.isLoading}
          emptyText="Доступу до панелі поки немає ні в кого."
          rowMuted={(u) => (u.is_active ? undefined : 0.55)}
          storageKey="platform-access-users"
        />

        {revoking && (
          <div className="card" style={{ marginTop: '0.75rem', background: 'var(--bg-soft)' }}>
            <p style={{ margin: '0 0 0.5rem', fontSize: '0.9rem' }}>
              Забрати доступ «{LEVEL_LABEL[revoking.level]}» у <b>{revoking.email}</b>?
              Це подіє одразу. Обліковий запис і його акаунти лишаться без змін.
            </p>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
              <input type="password" value={revokePassword} autoFocus
                aria-label="Ваш пароль для підтвердження"
                placeholder="ваш пароль" autoComplete="current-password"
                onChange={(e) => setRevokePassword(e.target.value)} style={{ maxWidth: '14rem' }} />
              <button type="button" className="danger" disabled={!revokePassword || revoke.isPending}
                onClick={() => { setRevokeError(null); revoke.mutate(); }}>
                {revoke.isPending ? 'забираємо…' : 'підтвердити'}
              </button>
              <button type="button" onClick={() => setRevoking(null)}>скасувати</button>
            </div>
            {revokeError && <div className="error">{revokeError}</div>}
          </div>
        )}
        {done && <p style={{ fontSize: '0.9rem', marginBottom: 0 }}>{done}</p>}
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Надати доступ</h3>
        <form onSubmit={onGrant}
          style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div className="field" style={{ margin: 0, minWidth: '16rem' }}>
            <label htmlFor="pa-email">Пошта користувача</label>
            <input id="pa-email" type="email" value={email} required
              placeholder="name@company.com" autoComplete="off"
              onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div className="field" style={{ margin: 0 }}>
            <label htmlFor="pa-level">Рівень</label>
            <select id="pa-level" value={level}
              onChange={(e) => setLevel(e.target.value as PlatformAccessLevel)}>
              <option value="SUPPORT">{LEVEL_LABEL.SUPPORT}</option>
              <option value="VIDEO">{LEVEL_LABEL.VIDEO}</option>
              <option value="OWNER" disabled={!canManageOwners}>{LEVEL_LABEL.OWNER}</option>
            </select>
          </div>
          <div className="field" style={{ margin: 0 }}>
            <label htmlFor="pa-password">Ваш пароль</label>
            <input id="pa-password" type="password" value={password} required
              autoComplete="current-password" onChange={(e) => setPassword(e.target.value)} />
          </div>
          <button type="submit" className="primary"
            disabled={grant.isPending || !email.trim() || !password}>
            {grant.isPending ? 'надаємо…' : 'надати доступ'}
          </button>
        </form>
        {!canManageOwners && q.data && (
          <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: 0 }}>
            Рівень «власник платформи» видає і забирає лише головний власник.
          </p>
        )}
        {level === 'OWNER' && (
          <p style={{ fontSize: '0.85rem', color: 'var(--badge-amber-fg)', marginBottom: 0 }}>
            Власник платформи змінює дані, які бачать усі клієнти (валюти, податкові
            константи, кредити). Давайте цей рівень лише тому, кому довіряєте як собі.
          </p>
        )}
        {error && <div className="error">{error}</div>}
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Журнал змін доступу</h3>
        <DataTable
          rows={q.data?.log ?? []}
          columns={logColumns}
          rowKey={(r) => `${r.created_at}|${r.action}|${r.level}|${r.target_email}`}
          loading={q.isLoading}
          emptyText="Змін ще не було."
          storageKey="platform-access-log"
        />
      </div>
    </>
  );
}
